import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { OshAttachment, OshAttachmentEntityType } from '../../../../interfaces/osh/oshattachment';
import { ConfirmationDialogComponent } from '../../../../components/dialogbox/dialog-confirmation.component';
import { ImagePreviewDialogComponent } from './image-preview-dialog/image-preview-dialog.component';
import { DataTableComponent, TableColumn } from '../../shared-components/data-table/data-table.component';
import { FileUploadDropzoneComponent } from '../../shared-components/file-upload-dropzone/file-upload-dropzone.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';

export interface OshAttachmentsDialogData {
    entityType: OshAttachmentEntityType;
    entityId: number;
    canEdit: boolean;
    contextLabel?: string;
}

@Component({
    selector: 'app-attachments-dialog',
    standalone: true,
    imports: [
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatProgressBarModule,
        DataTableComponent,
        FileUploadDropzoneComponent
    ],
    templateUrl: './attachments-dialog.component.html',
    styleUrls: ['./attachments-dialog.component.less'],
    encapsulation: ViewEncapsulation.None
})
export class OshAttachmentsDialogComponent implements OnInit {
    attachments: OshAttachment[] = [];
    isLoading = false;
    isUploading = false;
    private pendingUploads = 0;

    columns: TableColumn[] = [
        { key: 'fileName', header: 'File Name', width: '40%' },
        { key: 'createdBy', header: 'Uploaded By', width: '25%' },
        { key: 'createdAtUtc', header: 'Date', width: '20%', formatter: (value) => this.formatDate(value) },
        { key: 'actions', header: 'Actions', width: '15%', align: 'center' }
    ];

    constructor(
        private readonly http: HttpClient,
        @Inject('BASE_URL') private readonly baseUrl: string,
        @Inject(MAT_DIALOG_DATA) public data: OshAttachmentsDialogData,
        private readonly dialogRef: MatDialogRef<OshAttachmentsDialogComponent>,
        private readonly toastr: ToastrService,
        private readonly dialog: MatDialog
    ) {}

    ngOnInit(): void {
        this.loadAttachments();
    }

    loadAttachments(): void {
        this.isLoading = true;
        const url = `${this.baseUrl}api/osh/attachments/${this.data.entityType}/${this.data.entityId}`;
        
        this.http.get<OshAttachment[]>(url).subscribe({
            next: (result) => {
                this.attachments = result;
                this.isLoading = false;
            },
            error: (error: HttpErrorResponse) => {
                this.handleError('Failed to load attachments', error);
                this.isLoading = false;
            }
        });
    }

    onFilesSelected(files: File[]): void {
        for (const file of files) {
            this.uploadFile(file);
        }
    }

    onValidationError(message: string): void {
        this.toastr.warning(message, 'Validation Error');
    }

    uploadFile(file: File): void {
        this.pendingUploads++;
        this.isUploading = true;

        const formData = new FormData();
        formData.append('file', file, file.name);

        const url = `${this.baseUrl}api/osh/attachments/${this.data.entityType}/${this.data.entityId}`;

        const finalize = () => {
            this.pendingUploads--;
            if (this.pendingUploads === 0) {
                this.isUploading = false;
            }
        };

        this.http.post<{ success: boolean; id: number }>(url, formData).subscribe({
            next: () => {
                this.toastr.success(`File "${file.name}" uploaded successfully`, 'Success');
                this.loadAttachments();
                finalize();
            },
            error: (error: HttpErrorResponse) => {
                if (error.status === 403) {
                    this.toastr.error('You do not have permission to upload attachments', 'Permission Denied');
                } else if (error.status === 400 && error.error?.error) {
                    this.toastr.error(error.error.error, 'Upload Failed');
                } else {
                    this.handleError('Failed to upload file', error);
                }
                finalize();
            }
        });
    }

    viewAttachment(attachment: OshAttachment): void {
        const url = `${this.baseUrl}api/osh/attachments/${attachment.id}/download`;

        this.http.get(url, { responseType: 'blob' }).subscribe({
            next: (response) => {
                const blob = new Blob([response], { type: attachment.contentType });
                const blobUrl = globalThis.URL.createObjectURL(blob);

                const previewRef = this.dialog.open(ImagePreviewDialogComponent, {
                    data: {
                        fileUrl: blobUrl,
                        fileName: attachment.fileName,
                        contentType: attachment.contentType
                    },
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    panelClass: 'file-preview-dialog'
                });

                previewRef.afterClosed().subscribe(() => globalThis.URL.revokeObjectURL(blobUrl));
            },
            error: (error: HttpErrorResponse) => {
                this.handleError('Failed to load attachment', error);
            }
        });
    }

    downloadAttachment(attachment: OshAttachment): void {
        const url = `${this.baseUrl}api/osh/attachments/${attachment.id}/download`;

        this.http.get(url, { responseType: 'blob' }).subscribe({
            next: (response) => {
                const blob = new Blob([response], { type: attachment.contentType });
                const blobUrl = globalThis.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = attachment.fileName;
                link.click();
                globalThis.URL.revokeObjectURL(blobUrl);
            },
            error: (error: HttpErrorResponse) => {
                this.handleError('Failed to download attachment', error);
            }
        });
    }

    deleteAttachment(attachment: OshAttachment): void {
        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            data: {
                title: 'Delete Attachment',
                message: `Are you sure you want to delete "${attachment.fileName}"?`
            }
        });

        dialogRef.afterClosed().subscribe((result: { confirmed: boolean }) => {
            if (result?.confirmed) {
                const url = `${this.baseUrl}api/osh/attachments/${attachment.id}`;
                
                this.http.delete<{ success: boolean }>(url).subscribe({
                    next: () => {
                        this.toastr.success('Attachment deleted successfully', 'Success');
                        this.loadAttachments();
                    },
                    error: (error: HttpErrorResponse) => {
                        if (error.status === 403) {
                            this.toastr.error('You do not have permission to delete attachments', 'Permission Denied');
                        } else {
                            this.handleError('Failed to delete attachment', error);
                        }
                    }
                });
            }
        });
    }

    formatDate(dateString: string): string {
        if (!dateString) return '-';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '-';
        const pad = (n: number) => String(n).padStart(2, '0');
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
    }

    canPreview(contentType: string): boolean {
        return contentType.startsWith('image/') || contentType === 'application/pdf';
    }

    getFileIconColor(contentType: string): string {
        if (contentType.startsWith('image/')) {
            return '#9c27b0'; // purple for images
        }
        if (contentType === 'application/pdf') {
            return '#e53935'; // red for PDF
        }
        if (contentType.includes('spreadsheet') || contentType.includes('excel') || contentType === 'application/vnd.ms-excel') {
            return '#43a047'; // green for Excel
        }
        if (contentType.includes('word') || contentType.includes('document')) {
            return '#1e88e5'; // blue for Word
        }
        if (contentType.includes('presentation') || contentType.includes('powerpoint')) {
            return '#fb8c00'; // orange for PowerPoint
        }
        return '#757575'; // grey for others
    }

    getFileExtension(fileName: string): string {
        const parts = fileName.split('.');
        if (parts.length > 1) {
            return parts[parts.length - 1].toUpperCase();
        }
        return 'FILE';
    }

    close(): void {
        this.dialogRef.close();
    }

    private handleError(message: string, error: HttpErrorResponse): void {
        console.error(message, error);
        const errorMessage = error.error?.error || error.message || 'Unknown error';
        this.toastr.error(errorMessage, message);
    }
}
